import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
  DOCUMENT
} from '@angular/core';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import { MatFabButton } from '@angular/material/button';
import { MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { VexPageLayoutContentDirective } from '@vex/components/vex-page-layout/vex-page-layout-content.directive';
import { VexPageLayoutComponent } from '@vex/components/vex-page-layout/vex-page-layout.component';
import { VexSidebarComponent } from '../../../../@vex/components/vex-sidebar/vex-sidebar.component';
import { DashboardPreferenciaCardModel } from '../models/dashboard-config.model';
import { DashboardService } from '../services/dashboard.service';
import { DashboardCardChartComponent } from './card/chart/dashboard-card-chart.component';
import { DashboardCardComponent } from './card/dashboard-card.component';
import { DashboardSelecionarCardComponent } from './selecionar-card/dashboard-selecionar-card.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DashboardService],
  imports: [
    CdkDrag,
    CdkDropList,
    DashboardCardComponent,
    DashboardCardChartComponent,
    DashboardSelecionarCardComponent,
    MatFabButton,
    MatIcon,
    MatPrefix,
    VexPageLayoutComponent,
    VexPageLayoutContentDirective,
    VexSidebarComponent,
  ],
  host: { '(keydown)': 'onKeydown($event)' },
})
export class DashboardComponent {
  // Injectables
  private dashboardSvc = inject(DashboardService);
  private document = inject(DOCUMENT);

  // View child
  private selecionarCardSidebar = viewChild('selecionarCardSidebar', {
    read: VexSidebarComponent,
  });
  private guiaCard = viewChild('guiaCard', { read: ElementRef });
  private guiaGrid = viewChild('guiaGrid', { read: ElementRef });

  // Atributos private
  private resizeObserver = new ResizeObserver(
    this.resizeObserverCallback.bind(this)
  );

  // Atributos protected
  protected cardsPrefUsuario = signal<DashboardPreferenciaCardModel[]>([]);
  protected guiasArray = signal<null[]>([]);
  protected resizing = signal(false);

  // Effects
  private cardsEffect = effect(() => {
    const prefUsuario = this.dashboardSvc.getPreferenciaUsuario();
    this.cardsPrefUsuario.set(prefUsuario().cards);
  });

  // Lifecycle
  ngAfterViewInit(): void {
    const gridGuia = this.guiaGrid();
    if (gridGuia) {
      this.resizeObserver.observe(gridGuia.nativeElement);
    }
  }

  // Métodos protected
  protected guiasCount(gridGuia: HTMLDivElement, cardGuia: HTMLDivElement) {
    const colunas = Math.floor(gridGuia.clientWidth / cardGuia.clientWidth);
    const linhas = Math.floor(gridGuia.clientHeight / cardGuia.clientHeight);
    return [null];
  }

  protected onDropCard(event: CdkDragDrop<any>) {
    const cards = this.cardsPrefUsuario();
    moveItemInArray(cards, event.previousIndex, event.currentIndex);
    this.cardsPrefUsuario.set(cards);
    this.atualizarConfig();
  }

  protected onStartResize(
    event: MouseEvent,
    card: DashboardPreferenciaCardModel
  ) {
    event.preventDefault();

    const originalX = event.clientX;
    const originalY = event.clientY;
    const cardEl = (event.currentTarget as HTMLDivElement).parentElement;
    if (!cardEl) {
      return;
    }

    // Recupera tamanho inicial do card
    const originalColSpan = +(cardEl.dataset['cols'] || 1);
    const originalRowSpan = +(cardEl.dataset['rows'] || 1);
    const originalWidth = cardEl.offsetWidth;
    const originalHeight = cardEl.offsetHeight;
    const unitWidth = originalWidth / originalColSpan + 16; // 16 é o gap entre colunas/linhas
    const unitHeight = originalHeight / originalRowSpan + 16;
    let newColSpan = originalColSpan;
    let newRowSpan = originalRowSpan;
    this.resizing.set(true);

    const duringResize = (e: MouseEvent) => {
      const newWidth = originalWidth + e.clientX - originalX;
      newColSpan = Math.ceil(newWidth / unitWidth);
      cardEl.dataset['cols'] = newColSpan.toString();
      cardEl.style.gridColumn = `span ${newColSpan} / span ${newColSpan}`;
      cardEl.style.width = `${newWidth}px`;

      const newHeight = originalHeight + e.clientY - originalY;
      newRowSpan = Math.ceil(newHeight / unitHeight);
      cardEl.dataset['rows'] = newRowSpan.toString();
      cardEl.style.gridRow = `span ${newRowSpan} / span ${newRowSpan}`;
      cardEl.style.height = `${newHeight}px`;
    };

    const finishResize = (e: MouseEvent) => {
      this.resizing.set(false);
      this.document.removeEventListener('mousemove', duringResize);
      this.document.removeEventListener('mouseup', finishResize);
      cardEl.style.width = '';
      cardEl.style.height = '';

      if (card.colunasGrid !== newColSpan || card.linhasGrid !== newRowSpan) {
        card.colunasGrid = newColSpan;
        card.linhasGrid = newRowSpan;
        this.atualizarConfig();
      }
    };

    this.document.addEventListener('mousemove', duringResize);
    this.document.addEventListener('mouseup', finishResize);
  }

  protected onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.selecionarCardSidebar()?.close();
    }
  }

  // Métodos private
  private atualizarConfig() {
    this.dashboardSvc.savePreferenciaUsuario({
      cards: this.cardsPrefUsuario(),
    });
  }

  private resizeObserverCallback(entries: ResizeObserverEntry[]) {
    const gridGuia = entries[0].target;
    const cardGuia = this.guiaCard ? this.guiaCard()?.nativeElement : undefined;
    if (!cardGuia) {
      return;
    }

    const colunas =
      Math.floor(gridGuia.clientWidth / cardGuia.clientWidth) || 0;
    const linhas =
      Math.floor(gridGuia.clientHeight / cardGuia.clientHeight) || 0;
    const numGuias = colunas * linhas - 1;
    if (numGuias > 0 && numGuias !== this.guiasArray().length) {
      this.guiasArray.set(Array<null>(numGuias));
    }
  }
}
